import React, { useState } from 'react';

/**
 * DAS-1776: Regime colors for background bands
 * BULL = green (bullish), BEAR = red (bearish), NEUTRAL = blue/gray
 * Increased opacity for better visibility
 */
const REGIME_COLORS = {
  BULL: 'rgba(82, 196, 26, 0.25)',    // Light green
  BEAR: 'rgba(255, 77, 79, 0.25)',    // Light red (brighter)
  NEUTRAL: 'rgba(22, 119, 255, 0.15)', // Light blue
};

const SvgLineChart = ({ 
  series, 
  colors, 
  width = 740, 
  height = 220, 
  xLabels = [], 
  transactions = [], 
  dynamicColor = false,
  regimeBands = []  // DAS-1776: Array of { startIndex, endIndex, regime }
}) => {
  const [hoverIndex, setHoverIndex] = useState(null);
  const [hoverTransaction, setHoverTransaction] = useState(null);
  const pad = 32;
  const n = series[0]?.length || 0;
  if (!n) return <div style={{ color: '#999' }}>No data</div>;
  let minY = Infinity, maxY = -Infinity;
  for (const s of series) {
    for (const v of s) {
      if (v == null || isNaN(v)) continue;
      if (v < minY) minY = v;
      if (v > maxY) maxY = v;
    }
  }
  if (!isFinite(minY) || !isFinite(maxY) || minY === maxY) {
    minY = minY || 0;
    maxY = (maxY || 0) + 1;
  }
  // Add a small margin so lines don't hug the border
  const yMargin = (maxY - minY) * 0.05 || 1;
  minY -= yMargin;
  maxY += yMargin;

  const xFor = (i) => pad + (i * (width - 2 * pad)) / Math.max(1, n - 1);
  const yFor = (v) => pad + ((maxY - v) * (height - 2 * pad)) / (maxY - minY);

  // DAS-1638: Split path into segments based on positive/negative values for dynamic coloring
  const paths = series.map((s, si) => {
    // Only apply dynamic coloring if explicitly requested
    if (dynamicColor) {
      const segments = [];
      let currentSegment = [];
      let currentColor = null;
      
      for (let i = 0; i < s.length; i++) {
        const v = s[i];
        if (v == null || isNaN(v)) continue;
        
        // Determine color for this point (Green if positive/zero, red if negative)
        // User request: "green if on or above zero"
        const pointColor = v >= 0 ? '#52c41a' : '#cf1322';
        
        // If color changes, start a new segment
        if (currentColor !== null && currentColor !== pointColor && currentSegment.length > 0) {
          // Close current segment
          let d = '';
          for (let j = 0; j < currentSegment.length; j++) {
            const {x, y} = currentSegment[j];
            d += (d ? ' L ' : 'M ') + x + ' ' + y;
          }
          segments.push({d, color: currentColor});
          // Start new segment with last point of previous segment for continuity
          currentSegment = [currentSegment[currentSegment.length - 1]];
        }
        
        currentColor = pointColor;
        currentSegment.push({x: xFor(i), y: yFor(v)});
      }
      
      // Add final segment
      if (currentSegment.length > 0) {
        let d = '';
        for (let j = 0; j < currentSegment.length; j++) {
          const {x, y} = currentSegment[j];
          d += (d ? ' L ' : 'M ') + x + ' ' + y;
        }
        segments.push({d, color: currentColor});
      }
      
      return segments.map((seg, segIdx) => (
        <path key={`${si}-${segIdx}`} d={seg.d} fill="none" stroke={seg.color} strokeWidth="2" />
      ));
    } else {
      // Standard single-color path
      let d = '';
      for (let i = 0; i < s.length; i++) {
        const v = s[i];
        if (v == null || isNaN(v)) continue;
        const x = xFor(i);
        const y = yFor(v);
        d += (d ? ' L ' : 'M ') + x + ' ' + y;
      }
      return <path key={si} d={d} fill="none" stroke={colors[si]} strokeWidth="2" />;
    }
  });

  const yTicks = 4;
  const yTickVals = [];
  for (let i = 0; i <= yTicks; i++) {
    yTickVals.push(minY + ((maxY - minY) * i) / yTicks);
  }

  const handleMouseMove = (evt) => {
    const rect = evt.currentTarget.getBoundingClientRect();
    const x = evt.clientX - rect.left;
    const usable = width - 2 * pad;
    let t = (x - pad) / (usable || 1);
    t = Math.min(1, Math.max(0, t));
    const idx = Math.round(t * Math.max(0, n - 1));
    setHoverIndex(idx);
  };

  const handleMouseLeave = () => {
    setHoverIndex(null);
  };

  const hoverX = hoverIndex != null ? xFor(hoverIndex) : null;
  const hoverVals =
    hoverIndex != null
      ? series.map((s) => (s[hoverIndex] != null && !isNaN(s[hoverIndex]) ? s[hoverIndex] : null))
      : [];
  const hoverLabel =
    hoverIndex != null && xLabels && xLabels[hoverIndex] != null ? String(xLabels[hoverIndex]) : null;

  return (
    <svg
      width={width}
      height={height}
      style={{ border: '1px solid #eee', borderRadius: 4, background: '#fff' }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* DAS-1776: Regime background bands - rendered FIRST so they appear behind everything */}
      {regimeBands && regimeBands.length > 0 && regimeBands.map((band, idx) => {
        const x1 = xFor(band.startIndex);
        const x2 = xFor(Math.min(band.endIndex, n - 1));
        const bandWidth = x2 - x1;
        const color = REGIME_COLORS[band.regime] || REGIME_COLORS.NEUTRAL;
        return (
          <rect
            key={`regime-${idx}`}
            x={x1}
            y={pad}
            width={Math.max(bandWidth, 2)}
            height={height - 2 * pad}
            fill={color}
          />
        );
      })}
      {/* Axes */}
      <line x1={pad} y1={pad} x2={pad} y2={height - pad} stroke="#ccc" strokeWidth="1" />
      <line x1={pad} y1={height - pad} x2={width - pad} y2={height - pad} stroke="#ccc" strokeWidth="1" />
      {/* Y tick labels */}
      {yTickVals.map((val, i) => {
        const y = yFor(val);
        return (
          <g key={i}>
            <line x1={pad - 4} y1={y} x2={pad} y2={y} stroke="#ccc" strokeWidth="1" />
            <text x={pad - 6} y={y + 3} textAnchor="end" fontSize="10" fill="#666">
              {val.toFixed(2)}
            </text>
          </g>
        );
      })}
      {/* Data paths */}
      {paths}
      {/* Hover line and points */}
      {hoverIndex != null && (
        <g>
          <line
            x1={hoverX}
            y1={pad}
            x2={hoverX}
            y2={height - pad}
            stroke="#999"
            strokeWidth="1"
            strokeDasharray="4 4"
          />
          {hoverVals.map((v, si) => {
            if (v == null) return null;
            return (
              <circle
                key={si}
                cx={hoverX}
                cy={yFor(v)}
                r={3}
                fill={colors[si]}
                stroke="#fff"
                strokeWidth="1"
              />
            );
          })}
          {hoverLabel && (
            <rect
              x={Math.min(hoverX + 8, width - pad - 120)}
              y={pad + 4}
              width={120}
              height={14 + 10 * series.length}
              fill="rgba(255,255,255,0.9)"
              stroke="#ccc"
            />
          )}
          {hoverLabel && (
            <text
              x={Math.min(hoverX + 12, width - pad - 116)}
              y={pad + 14}
              fontSize="10"
              fill="#000"
            >
              {hoverLabel}
            </text>
          )}
          {hoverLabel &&
            hoverVals.map((v, si) => (
              <text
                key={si}
                x={Math.min(hoverX + 12, width - pad - 116)}
                y={pad + 14 + (si + 1) * 10}
                fontSize="10"
                fill={colors[si]}
              >
                {v != null ? v.toFixed(2) : ''}
              </text>
            ))}
        </g>
      )}
      {/* DAS-1611: Transaction markers */}
      {transactions && transactions.length > 0 && transactions.map((tx, txIdx) => {
        const txX = xFor(tx.index);
        const txY = yFor(tx.value);
        const isBuy = tx.type === 'BUY';
        // DAS-1638: User request: Blue dots for BUY, Red dots for SELL
        const color = isBuy ? '#1677ff' : '#cf1322';
        return (
          <g key={`tx-${txIdx}`}>
            <circle
              cx={txX}
              cy={txY}
              r={4}
              fill={color}
              stroke="#fff"
              strokeWidth="1.5"
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => setHoverTransaction(tx)}
              onMouseLeave={() => setHoverTransaction(null)}
            />
          </g>
        );
      })}
      {/* DAS-1611: Transaction hover tooltip */}
      {hoverTransaction && (
        <g>
          <rect
            x={Math.min(xFor(hoverTransaction.index) + 8, width - pad - 180)}
            y={pad + 4}
            width={180}
            height={80}
            fill="rgba(255,255,255,0.95)"
            stroke="#ccc"
            strokeWidth="1"
            rx="4"
          />
          <text
            x={Math.min(xFor(hoverTransaction.index) + 14, width - pad - 174)}
            y={pad + 18}
            fontSize="11"
            fontWeight="bold"
            fill="#000"
          >
            {hoverTransaction.type === 'BUY' ? 'Buy' : hoverTransaction.type === 'SELL' ? 'Sell' : hoverTransaction.type} - {hoverTransaction.ticker}
          </text>
          <text
            x={Math.min(xFor(hoverTransaction.index) + 14, width - pad - 174)}
            y={pad + 32}
            fontSize="10"
            fill="#666"
          >
            Date: {hoverTransaction.date}
          </text>
          <text
            x={Math.min(xFor(hoverTransaction.index) + 14, width - pad - 174)}
            y={pad + 46}
            fontSize="10"
            fill="#666"
          >
            Qty: {Number(hoverTransaction.qty || 0).toFixed(2)} @ €{Number(hoverTransaction.price || 0).toFixed(2)}
          </text>
          <text
            x={Math.min(xFor(hoverTransaction.index) + 14, width - pad - 174)}
            y={pad + 60}
            fontSize="10"
            fill="#666"
          >
            Cash: €{Number(hoverTransaction.cashEffect || 0).toFixed(2)}
          </text>
          <text
            x={Math.min(xFor(hoverTransaction.index) + 14, width - pad - 174)}
            y={pad + 74}
            fontSize="10"
            fill="#666"
          >
            Fees: €{Number(hoverTransaction.fees || 0).toFixed(2)}
          </text>
        </g>
      )}
    </svg>
  );
};

export default SvgLineChart;
